/**
 * Les cahiers ouvrables pour un enfant.
 *
 * Le périmètre est **dérivé** et jamais déclaré à la main : matières
 * principales de l'enfant ∩ ouvrages ingérés. Une matière principale sans
 * cahier n'apparaît donc pas, et c'est voulu — un onglet qui s'ouvre sur rien a
 * l'apparence exacte d'une panne.
 *
 * Reste qu'une liste vide a **deux causes**, que le serveur distingue
 * (`manque`) et que cet écran écrit en clair, parce qu'elles appellent deux
 * gestes opposés : sans matières principales fixées il n'y a pas de périmètre,
 * et cela se répare d'une commande au poste ; avec des matières mais sans
 * cahier, il n'y a rien à réparer, il manque un livre. Les résumer en un même
 * « rien à afficher » enverrait chercher une panne là où il n'y en a pas.
 */

import { Link, useParams } from "react-router-dom";

import { useContenuDOuvrage } from "@/ui/enCache";
import { useEnfants } from "@/ui/enfants";
import { etiquette } from "@/ui/matieres";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { entrainementsDe } from "@/v2/chemins";

/** Les deux causes, dans les mots d'un parent. */
const MANQUE: Record<string, string> = {
  aucune_matiere_principale:
    "Aucune matière principale n'est fixée pour cet enfant : rien ne dit encore " +
    "sur quoi le faire travailler. Cela se règle depuis le poste, avec " +
    "« teacher-people set-main-subjects ».",
  aucun_ouvrage:
    "Les matières principales de cet enfant n'ont pas encore de cahier découpé. " +
    "Il n'y a rien à réparer ici : il manque un ouvrage.",
};

export function CatalogueScreen() {
  const { childId = "" } = useParams();
  const enfants = useEnfants();
  const prenom = enfants.find((enfant) => enfant.child_id === childId)?.display_name;
  // Gardé dans le navigateur le temps de la session : le catalogue ne change
  // pas entre deux allers-retours vers un cahier (`ui/enCache`).
  const { donnees: catalogue, erreur } = useContenuDOuvrage("parascolaireOuvrages", {
    params: { child_id: childId },
  });

  const ouvrages = catalogue?.ouvrages ?? [];

  return (
    <Screen
      title="Les cahiers"
      lead={prenom ? `Ce que l'on peut faire travailler à ${prenom}` : undefined}
    >
      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!catalogue && !erreur ? <Loading /> : null}

      {catalogue && ouvrages.length === 0 ? (
        <Card>
          <Empty>{MANQUE[catalogue.manque ?? ""] ?? "Aucun cahier pour cet enfant."}</Empty>
        </Card>
      ) : null}

      {ouvrages.map((ouvrage) => (
        <Card key={ouvrage.cle}>
          <Link to={`${entrainementsDe(childId)}/${ouvrage.cle}`} className="list__item">
            <strong>{ouvrage.titre}</strong>
          </Link>
          <p className="muted small">
            {etiquette(ouvrage.matiere, ouvrage.matiere_libelle)} — {ouvrage.chapitres} chapitres,{" "}
            {ouvrage.exercices} exercices
          </p>
        </Card>
      ))}
    </Screen>
  );
}
